import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/pathways", label: "Pathways", testid: "nav-pathways" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/leadership", label: "Leadership", testid: "nav-leadership" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/resources", label: "Resources", testid: "nav-resources" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl ${
        scrolled
          ? "bg-[#F6F1E6]/95 border-b border-[#1F2A1B]/10 shadow-[0_4px_20px_-12px_rgba(31,42,27,0.25)]"
          : "bg-[#F6F1E6]/85 border-b border-[#1F2A1B]/5"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-4 md:px-8 lg:px-12 py-2 md:py-3 flex items-center justify-between gap-4 md:gap-6">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src="/logo.png"
            alt="Carbon on Country"
            className="h-16 sm:h-20 md:h-24 lg:h-28 xl:h-32 2xl:h-[144px] w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <span className="sr-only">Carbon on Country</span>
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-5 xl:gap-7 2xl:gap-9 flex-1 justify-end">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-[10px] lg:text-[11px] xl:text-[12px] tracking-[0.18em] lg:tracking-[0.22em] xl:tracking-[0.26em] uppercase font-semibold transition-colors duration-300 whitespace-nowrap ${
                  isActive
                    ? "text-[#3F5132]"
                    : "text-[#4F5A48] hover:text-[#1F2A1B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta-talk"
          className="hidden 2xl:inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#1F2A1B] border border-[#C45A3C] px-5 py-3 hover:bg-[#C45A3C] transition-colors whitespace-nowrap shrink-0"
        >
          Work with us
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#1F2A1B] p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-[#F6F1E6] border-t border-[#1F2A1B]/10 px-6 py-8"
        >
          <nav className="flex flex-col gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.28em] uppercase font-semibold transition-colors ${
                    isActive
                      ? "text-[#3F5132]"
                      : "text-[#4F5A48]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
